import React from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";

const DoubtSupportBarGrap = ({
  statistics = null,
  monthsData = [],
  weekData = [],
  view = "total", // 🔑 CHANGED: default Total
  setView = () => {},
  year = new Date().getFullYear(),
  setYear = () => {},
  month = new Date().getMonth(), // 0-11
  setMonth = () => {},
  weekIndex = 0,
  setWeekIndex = () => {},
}) => {
  if (!statistics) {
    return <div className="text-center p-4">Loading chart...</div>;
  }

  const { months, weeks, selectedWeek, totals } = statistics; // 🔑 totals added

  // ================= DATA MAPPING =================
  let labels = [];
  let initiated = [];
  let closed = [];
  let unresolved = [];
  let dateMap = []; // 🔑 for tooltip date

  if (view === "total") {
    labels = ["Total"]; // 🔑 NEW
    initiated = [Number(totals?.initiated || 0)];
    closed = [Number(totals?.closed || 0)];
    unresolved = [Number(totals?.unresolved || 0)];
    dateMap = [];
  } else if (view === "year") {
    labels = months.map((m) => m.month);
    initiated = months.map((m) => m.initiated);
    closed = months.map((m) => m.closed);
    unresolved = months.map((m) => m.unresolved);
    dateMap = [];
  } else if (view === "month") {
    const allDays = weeks
      .flatMap((w) => w.days || [])
      .filter((d) => d.isCurrentMonth);

    labels = allDays.map((d) => new Date(d.date).getDate());
    initiated = allDays.map((d) => d.initiated);
    closed = allDays.map((d) => d.closed);
    unresolved = allDays.map((d) => d.unresolved);
    dateMap = allDays.map((d) => d.date);
  } else {
    const days = selectedWeek?.days || [];
    labels = days.map((d) =>
      new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }),
    );
    initiated = days.map((d) => d.initiated);
    closed = days.map((d) => d.closed);
    unresolved = days.map((d) => d.unresolved);
    dateMap = days.map((d) => d.date);
  }

  // ================= TOTALS =================
  const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

  const getTrend = (data) => {
    if (data.length < 2) return { dir: "up", percent: 0 };
    const diff = data[data.length - 1] - data[0];
    const percent =
      data[0] === 0 ? 0 : Math.abs((diff / data[0]) * 100).toFixed(1);
    return { dir: diff >= 0 ? "up" : "down", percent };
  };

  const cardTotals = {
    initiated: sum(initiated),
    closed: sum(closed),
    unresolved: sum(unresolved),
    total: sum(initiated) + sum(closed) + sum(unresolved),
  };

  const cardTrends = {
    initiated: getTrend(initiated),
    closed: getTrend(closed),
    unresolved: getTrend(unresolved),
    total: getTrend(initiated.map((v, i) => v + closed[i] + unresolved[i])),
  };

  // ================= SPARK OPTIONS (UNCHANGED) =================
  const sparkOptions = (color) => ({
    chart: { type: "area", sparkline: { enabled: true } },
    stroke: { curve: "smooth", width: 3 },
    colors: [color],
    fill: {
      type: "gradient",
      gradient: { opacityFrom: 0.35, opacityTo: 0 },
    },
    tooltip: {
      enabled: true,
      followCursor: true,
      custom: ({ dataPointIndex }) => {
        const dateStr = dateMap[dataPointIndex];
        if (!dateStr) return "";

        const dateObj = new Date(dateStr);
        const dayName = dateObj.toLocaleDateString("en-US", {
          weekday: "short",
        });
        const fullDate = dateObj.toISOString().split("T")[0];

        return `
          <div style="
            background:#ffffff;
            padding:10px 12px;
            border-radius:6px;
            box-shadow:0 4px 12px rgba(0,0,0,0.12);
            font-family:Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
            font-size:13px;
            color:#111827;
            min-width:180px;
          ">
            <div style="font-weight:600; margin-bottom:6px;">
              ${dayName}, ${fullDate}
            </div>
            <div style="line-height:1.6;">
              <div>Initiated: ${initiated[dataPointIndex] || 0}</div>
              <div>Solved: ${closed[dataPointIndex] || 0}</div>
              <div>Unresolved: ${unresolved[dataPointIndex] || 0}</div>
            </div>
          </div>
        `;
      },
    },
  });

  const CardBox = ({ title, value, data, color, trend }) => (
    <Card style={{ borderRadius: 12 }}>
      <Card.Body>
        <div className="d-flex justify-content-between">
          <h4>{value}</h4>
          <span
            style={{
              color: trend.dir === "up" ? "green" : "red",
              fontSize: 13,
            }}
          >
            {trend.dir === "up" ? "▲" : "▼"} {trend.percent}%
          </span>
        </div>
        <div className="text-muted mb-2">{title}</div>
        <ReactApexChart
          type="area"
          height={70}
          series={[{ data }]}
          options={sparkOptions(color)}
        />
      </Card.Body>
    </Card>
  );

  // ================= RENDER =================
  return (
    <div style={{ padding: 20 }}>
      <Row className="g-2 mb-3 align-items-center">

        {/* 🔑 NEW: TOTAL BUTTON */}
        <Col md="auto">
          <Button
            variant={view === "total" ? "primary" : "outline-primary"}
            onClick={() => setView("total")}
          >
            Total
          </Button>
        </Col>

        <Col md="auto">
          <Button
            variant={view === "week" ? "primary" : "outline-primary"}
            onClick={() => setView("week")}
          >
            Week
          </Button>
        </Col>

        <Col md="auto">
          <Button
            variant={view === "month" ? "primary" : "outline-primary"}
            onClick={() => setView("month")}
          >
            Month
          </Button>
        </Col>

        <Col md="auto">
          <Button
            variant={view === "year" ? "primary" : "outline-primary"}
            onClick={() => setView("year")}
          >
            Year
          </Button>
        </Col>

        <Col md={2}>
          <Form.Select value={year} onChange={(e) => setYear(+e.target.value)}>
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y}>{y}</option>
            ))}
          </Form.Select>
        </Col>

        {(view === "week" || view === "month") && (
          <Col md={2}>
            <Form.Select
              value={month}
              onChange={(e) => setMonth(+e.target.value)}
            >
              {[
                "Jan","Feb","Mar","Apr","May","Jun",
                "Jul","Aug","Sep","Oct","Nov","Dec",
              ].map((m, i) => (
                <option key={i} value={i}>
                  {m}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}

        {view === "week" && (
          <Col md="auto">
            <Form.Select
              value={weekIndex}
              onChange={(e) => setWeekIndex(+e.target.value)}
            >
              {weeks.map((_, i) => (
                <option key={i} value={i}>
                  Week {i + 1}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
      </Row>

      <Row className="g-4">
        <Col md={3}>
          <CardBox
            title="Initiated"
            value={cardTotals.initiated}
            data={initiated}
            color="#008FFB"
            trend={cardTrends.initiated}
          />
        </Col>
        <Col md={3}>
          <CardBox
            title="Closed"
            value={cardTotals.closed}
            data={closed}
            color="#00E396"
            trend={cardTrends.closed}
          />
        </Col>
        <Col md={3}>
          <CardBox
            title="Unresolved"
            value={cardTotals.unresolved}
            data={unresolved}
            color="#FEB019"
            trend={cardTrends.unresolved}
          />
        </Col>
        <Col md={3}>
          <CardBox
            title="Total"
            value={cardTotals.total}
            data={initiated.map((v, i) => v + closed[i] + unresolved[i])}
            color="#6366F1"
            trend={cardTrends.total}
          />
        </Col>
      </Row>
    </div>
  );
};

export default DoubtSupportBarGrap;